import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Bookmark, BookmarkCheck, Sparkles, Info, ShoppingCart, X, CheckCircle2,
  UserPlus, ShieldCheck, AlertTriangle, Trophy, Trash2,
  FileText, Image, Video, ChevronDown, ChevronUp, Send,
  type LucideIcon,
} from "lucide-react";
import { type Blogger, useMembership } from "@/shared/api/wb";
import { Avatar } from "@/shared/ui/Avatar";
import { InfoTip } from "@/shared/ui";
import { AddToCollectionModal } from "@/shared/ui/AddToCollectionModal";
import { InboxModal } from "@/pages/InboxModal";
import { cn } from "@/shared/lib/cn";
import { fmtNum, fmtRub } from "@/shared/lib/format";
import {
  FORMATS, AI_INSIGHTS, PLATFORM_LABELS,
  POSEV_PLATFORMS, NATIV_PLATFORMS,
  type FormatType, type PlatformKey, type FormatEntry,
  getFormats, normalizePlatform, getDefaultPlatformForType,
} from "./data";

// ─── Public types ────────────────────────────────────────────────────────────

export type CartSelection = {
  type: FormatType;
  platform: PlatformKey;
  formatId: string;
  formatName: string;
  price: number;
  cpv: number;
  placementDate: string;
};

export type BloggerCardProps = {
  blogger: Blogger;
  context?: "catalog" | "campaign" | "basket";
  // catalog / campaign: controlled in-cart state
  isInCart?: boolean;
  cartSelections?: CartSelection[];
  onCartAdd?: (sel: CartSelection) => void;
  onCartRemove?: (sel: CartSelection) => void;
  // basket: read-only display data
  cartSelection?: CartSelection;
  onPlacementDateChange?: (date: string) => void;
  // lock to posev (campaign seeding mode) or to nativ (campaign native mode)
  seedingLocked?: boolean;
  nativeLocked?: boolean;
  // why this blogger was recommended
  reason?: string;
  reason_type?: string;
};

// ─── Mock helpers ─────────────────────────────────────────────────────────────

function mockHash(id: string): number {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) & 0x0fffffff;
  return h;
}

function mockMale(id: string): number  { return 32 + (mockHash(id + "m") % 38); }
function mockLive(id: string): number  { return 84 + (mockHash(id + "l") % 13); }

// ─── Avatar platform colors ───────────────────────────────────────────────────

function avatarGrad(platform: string): [string, string] {
  if (platform === "telegram" || platform === "tg") return ["#9FE1CB", "#085041"];
  if (platform === "vk") return ["#B5D4F4", "#0C447C"];
  return ["#BFF93F", "#1C1C1C"];
}

// ─── Platform chip label ──────────────────────────────────────────────────────

const PLATFORM_BADGE: Record<string, { label: string; bg: string; color: string }> = {
  telegram: { label: "TG",  bg: "#E1F5EE", color: "#0F6E56" },
  tg:       { label: "TG",  bg: "#E1F5EE", color: "#0F6E56" },
  vk:       { label: "VK",  bg: "#E6F1FB", color: "#185FA5" },
  youtube:  { label: "YT",  bg: "#FDECEA", color: "#C62828" },
  yt:       { label: "YT",  bg: "#FDECEA", color: "#C62828" },
  dzen:     { label: "ДЗН", bg: "#F0F0F0", color: "#444"    },
  tiktok:   { label: "TT",  bg: "#ECE8FF", color: "#4A1FA8" },
  tt:       { label: "TT",  bg: "#ECE8FF", color: "#4A1FA8" },
};

function PlatformBadge({ platform }: { platform: string }) {
  const cfg = PLATFORM_BADGE[platform.toLowerCase()]
    ?? { label: platform.slice(0, 2).toUpperCase(), bg: "#F0F0F0", color: "#555" };
  return (
    <span
      className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-display font-bold"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

// ─── Format Toggle ────────────────────────────────────────────────────────────

function FormatToggle({
  value, onChange, disabledNativ, disabledPosev,
}: {
  value: FormatType;
  onChange: (v: FormatType) => void;
  disabledNativ?: boolean;
  disabledPosev?: boolean;
}) {
  return (
    <div
      className="inline-flex rounded-[20px] p-[2px] gap-px"
      style={{ background: "var(--wb-ink-06, rgba(28,28,28,0.06))" }}
    >
      {(["posev", "nativ"] as FormatType[]).map((t) => {
        const active = value === t;
        const isPosev = t === "posev";
        const isDisabled = isPosev ? disabledPosev : disabledNativ;
        return (
          <button
            key={t}
            disabled={isDisabled}
            onClick={() => onChange(t)}
            className={cn(
              "px-2.5 py-1 rounded-[18px] text-[10px] font-display font-bold transition-colors leading-none",
              active
                ? isPosev
                  ? "text-[#27500A] border border-[#C0DD97]"
                  : "text-[#185FA5] border border-[#85B7EB]"
                : "text-ink-40 border border-transparent hover:text-ink-60",
              isDisabled && "opacity-40 cursor-not-allowed",
            )}
            style={active ? { background: isPosev ? "#EAF3DE" : "#E6F1FB" } : {}}
          >
            {isPosev ? "Посев" : "Натив"}
          </button>
        );
      })}
    </div>
  );
}

// ─── Platform Chips ───────────────────────────────────────────────────────────

const CHIP_COLORS: Record<PlatformKey, { bg: string; text: string; activeBg: string; activeText: string }> = {
  tg:   { bg: "#F0FBFF", text: "#0F6E56", activeBg: "#E1F5EE", activeText: "#085041" },
  vk:   { bg: "#EDF4FD", text: "#185FA5", activeBg: "#E6F1FB", activeText: "#0C447C" },
  max:  { bg: "#F3EEFF", text: "#5B2DBE", activeBg: "#E9DEFF", activeText: "#3D179A" },
  yt:   { bg: "#FFF0F0", text: "#B30000", activeBg: "#FFE0E0", activeText: "#8B0000" },
  dzen: { bg: "#F5F5F5", text: "#444",    activeBg: "#E8E8E8", activeText: "#1C1C1C" },
  tt:   { bg: "#F0F0F2", text: "#2C3036", activeBg: "#E0E0E5", activeText: "#1A1D22" },
  lk:   { bg: "#FFF4EE", text: "#A63000", activeBg: "#FFE8D5", activeText: "#7A2200" },
};

function PlatformChips({
  type, value, onChange, compact,
}: {
  type: FormatType;
  value: PlatformKey;
  onChange: (p: PlatformKey) => void;
  compact?: boolean;
}) {
  const platforms = type === "posev" ? POSEV_PLATFORMS : NATIV_PLATFORMS;
  return (
    <div
      className={cn("flex gap-1", compact ? "overflow-x-auto flex-nowrap" : "flex-wrap")}
      style={compact ? { scrollbarWidth: "none" } : {}}
    >
      {platforms.map((p) => {
        const active = value === p;
        const c = CHIP_COLORS[p];
        return (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={cn(
              "flex-shrink-0 rounded-full font-display font-bold transition-colors",
              compact ? "text-[10px]" : "h-5 px-2 text-[10px]",
            )}
            style={{
              background: active ? c.activeBg : c.bg,
              color: active ? c.activeText : c.text,
              outline: active ? `1.5px solid ${c.activeText}` : "none",
              ...(compact ? { padding: "2px 7px" } : {}),
            }}
          >
            {PLATFORM_LABELS[p]}
          </button>
        );
      })}
    </div>
  );
}

// ─── AI Insight ───────────────────────────────────────────────────────────────

function AiInsight({ formatId, type, size = "sm" }: { formatId: string; type: FormatType; size?: "sm" | "md" }) {
  const text = AI_INSIGHTS[formatId];
  if (!text) return null;
  const isPosev = type === "posev";
  const Icon = isPosev ? Sparkles : Info;
  const color = isPosev ? "#639922" : "#378ADD";
  return (
    <div className={cn("flex items-start gap-1", size === "sm" ? "text-[11px]" : "text-[12px]")} style={{ color: "var(--fg-3, rgba(28,28,28,0.4))" }}>
      <Icon size={size === "sm" ? 12 : 13} style={{ color, flexShrink: 0, marginTop: 1 }} />
      <span className="leading-tight line-clamp-1">{text}</span>
    </div>
  );
}

// ─── Quality Score SVG ────────────────────────────────────────────────────────

// ─── Metric inline ────────────────────────────────────────────────────────────

function InlineMetric({ v, label, tip }: { v: string; label: string; tip?: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-[13px] font-mono font-[500] tabular-nums leading-none">{v}</span>
      <div className="flex items-center gap-0.5 mt-[2px]">
        <span className="text-[10px] text-ink-40 leading-none">{label}</span>
        {tip && <InfoTip align="center">{tip}</InfoTip>}
      </div>
    </div>
  );
}

// ─── Detail Tabs ──────────────────────────────────────────────────────────────

type Tab = "audience" | "fraud" | "history" | "content";

function AudienceTab({ blogger }: { blogger: Blogger }) {
  const male = mockMale(blogger.id);
  const female = 100 - male;
  const geo = [
    { city: "Москва", pct: 38 + (mockHash(blogger.id + "g") % 12) },
    { city: "СПб", pct: 12 + (mockHash(blogger.id + "g2") % 8) },
    { city: "Другие", pct: 100 },
  ];
  geo[2].pct = 100 - geo[0].pct - geo[1].pct;
  return (
    <div className="grid gap-4">
      <div className="flex items-center gap-3">
        <div className="flex-1 h-3 rounded-full overflow-hidden flex">
          <div className="h-full" style={{ width: `${male}%`, background: "#5B8DEF" }} />
          <div className="h-full flex-1" style={{ background: "#F472B6" }} />
        </div>
        <span className="text-[12px] text-ink-60 whitespace-nowrap">М {male}% · Ж {female}%</span>
      </div>
      <div className="grid gap-1">
        <div className="text-[10px] text-ink-40 font-display font-semibold uppercase tracking-wider mb-1">Основной возраст</div>
        <div className="text-[14px] font-display font-bold">25–34 <span className="text-[12px] font-normal text-ink-40">· {28 + (mockHash(blogger.id + "a") % 20)}%</span></div>
      </div>
      <div className="grid gap-1.5">
        <div className="text-[10px] text-ink-40 font-display font-semibold uppercase tracking-wider">Гео</div>
        {geo.map((g) => (
          <div key={g.city} className="flex items-center gap-2">
            <span className="w-14 text-[11px] text-ink-60">{g.city}</span>
            <div className="flex-1 h-1.5 rounded-full bg-ink-12 overflow-hidden">
              <div className="h-full rounded-full bg-lime" style={{ width: `${Math.min(100, g.pct * 1.8)}%` }} />
            </div>
            <span className="w-8 text-right text-[11px] font-mono text-ink-60">{g.pct}%</span>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-ink-40 flex items-start gap-1">
        <Sparkles size={12} style={{ color: "#639922", marginTop: 1 }} />
        <span>Аудитория хорошо совпадает с вашим продуктом — высокий overlap по интересам</span>
      </div>
    </div>
  );
}

function FraudTab({ blogger }: { blogger: Blogger }) {
  const live = mockLive(blogger.id);
  const items = [
    { label: "Живая аудитория", value: `${live}%`, status: live >= 85 ? "ok" : live >= 70 ? "warn" : "bad" },
    { label: "Качество активности", value: "Высокое", status: "ok" },
    { label: "Рост подписчиков", value: "Органический", status: "ok" },
    { label: "Масс-фолловинг", value: "Нет", status: "ok" },
  ] as const;
  const statusStyle = (s: "ok" | "warn" | "bad") =>
    s === "ok"   ? { background: "#EAF3DE", color: "#27500A" }
    : s === "warn" ? { background: "#FAEEDA", color: "#633806" }
    : { background: "#FCEBEB", color: "#791F1F" };
  const allOk = items.every((i) => i.status === "ok");
  return (
    <div className="grid gap-2">
      {items.map((item) => (
        <div key={item.label} className="flex items-center justify-between py-1 border-b border-ink-12/50 last:border-b-0">
          <span className="text-[12.5px] text-ink-60">{item.label}</span>
          <span className="text-[11px] font-display font-bold px-2 py-0.5 rounded-full" style={statusStyle(item.status)}>
            {item.value}
          </span>
        </div>
      ))}
      <div className="mt-2 flex items-center gap-1.5 text-[11px]" style={{ color: allOk ? "#27500A" : "#633806" }}>
        {allOk
          ? <><CheckCircle2 size={13} /> Профиль чистый — аудитория органическая</>
          : <><AlertTriangle size={13} /> Обнаружены незначительные отклонения — рекомендуем уточнить детали</>}
      </div>
    </div>
  );
}

function HistoryTab({ blogger }: { blogger: Blogger }) {
  const hash = mockHash(blogger.id);
  const history = [
    { brand: "Skyeng", type: "Посев · 1/24", date: "фев 2025", cpv: `${(0.31 + (hash % 10) * 0.02).toFixed(2)} ₽`, result: "ok" },
    { brand: "Сбер Маркет", type: "Натив · Пост", date: "янв 2025", cpv: `${(0.55 + (hash % 8) * 0.03).toFixed(2)} ₽`, result: "great" },
    { brand: "Lamoda", type: "Посев · 2/48", date: "дек 2024", cpv: `${(0.38 + (hash % 6) * 0.02).toFixed(2)} ₽`, result: "ok" },
  ];
  const resultStyle = (r: string) =>
    r === "great"
      ? { background: "#EAF3DE", color: "#27500A" }
      : { background: "#E6F1FB", color: "#185FA5" };
  const resultLabel = (r: string) => r === "great" ? "Перевыполнен" : "Выполнено в срок";
  return (
    <div className="grid gap-2">
      {history.map((h) => (
        <div key={h.brand} className="flex items-center justify-between py-1.5 border-b border-ink-12/50 last:border-b-0">
          <div>
            <div className="text-[13px] font-display font-semibold">{h.brand}</div>
            <div className="text-[11px] text-ink-40">{h.type} · {h.date}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-mono text-ink-60">{h.cpv}</div>
            <span className="text-[10px] font-display font-bold px-1.5 py-0.5 rounded-full" style={resultStyle(h.result)}>
              {resultLabel(h.result)}
            </span>
          </div>
        </div>
      ))}
      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-ink-40">
        <Trophy size={13} className="text-lime" /> Выполнено в срок в 100% кампаний · Avg CPV на 14% ниже рынка
      </div>
    </div>
  );
}

function ContentTab({ blogger: _ }: { blogger: Blogger }) {
  const icons = [FileText, Image, Video, Image, FileText, Video, Image, FileText, Image];
  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-3 gap-2">
        {icons.map((Icon, i) => (
          <div key={i} className="aspect-square rounded-md bg-ink-6 flex items-center justify-center">
            <Icon size={20} className="text-ink-40" />
          </div>
        ))}
      </div>
      <div className="text-[11px] text-ink-40 flex items-start gap-1">
        <Info size={12} style={{ marginTop: 1 }} />
        <span>Последний пост — 2 дня назад · Частота — 4–5 постов/нед · Лучшее время 18:00–21:00</span>
      </div>
    </div>
  );
}

// ─── Full expanded card ────────────────────────────────────────────────────────

function CardExpanded({
  blogger, onClose,
}: {
  blogger: Blogger;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>("audience");

  const tabs: { key: Tab; label: string }[] = [
    { key: "audience", label: "Аудитория" },
    { key: "fraud",    label: "Качество" },
    { key: "history",  label: "История РК" },
    { key: "content",  label: "Контент" },
  ];

  return (
    <div className="border-t border-ink-12 pt-3 pb-3 px-4 fade-up">
      <div className="flex gap-0 border-b border-ink-12 mb-3">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={cn("px-3 py-2 text-[12.5px] font-display font-semibold border-b-2 -mb-px transition-colors",
              tab === t.key ? "border-ink text-ink" : "border-transparent text-ink-40 hover:text-ink-60")}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="px-1">
        {tab === "audience" && <AudienceTab blogger={blogger} />}
        {tab === "fraud"    && <FraudTab blogger={blogger} />}
        {tab === "history"  && <HistoryTab blogger={blogger} />}
        {tab === "content"  && <ContentTab blogger={blogger} />}
      </div>
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-ink-12">
        <Link
          to={`/blogger/${blogger.id}`}
          className="text-[11px] text-link hover:underline inline-flex items-center gap-0.5"
        >
          Открыть полный профиль →
        </Link>
        <button onClick={onClose}
          className="inline-flex items-center gap-0.5 text-[11px] text-ink-40 hover:text-ink">
          <ChevronUp size={11} /> Свернуть
        </button>
      </div>
    </div>
  );
}

// ─── Basket card ──────────────────────────────────────────────────────────────

export function BloggerCardBasket({
  blogger, cartSelection, onRemove, onPlacementDateChange,
}: {
  blogger: Blogger;
  cartSelection: CartSelection;
  onRemove?: () => void;
  onPlacementDateChange?: (date: string) => void;
}) {
  const [inboxOpen, setInboxOpen] = useState(false);
  const isPosev = cartSelection.type === "posev";
  const typeColor = isPosev ? { bg: "#EAF3DE", text: "#27500A" } : { bg: "#E6F1FB", text: "#185FA5" };

  const cpm = blogger.reach > 0 ? (cartSelection.price / blogger.reach) * 1000 : 0;

  return (
    <div className="rounded-lg bg-white p-4 grid gap-3" style={{ border: "2px solid #97C459" }}>
      {/* Row 1: Avatar + name + badges */}
      <div className="flex items-center gap-3 flex-wrap">
        <Avatar name={blogger.full} size={36} grad={avatarGrad(blogger.platform) as [string,string]} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-semibold text-[14px]">{blogger.full}</span>
            <PlatformBadge platform={blogger.platform} />
            <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-display font-bold"
              style={{ background: "#EAF3DE", color: "#27500A" }}>
              <CheckCircle2 size={10} /> В корзине
            </span>
          </div>
          <div className="text-[11px] text-ink-40">{blogger.name}</div>
          {blogger.description && (
            <div className="text-[11.5px] text-ink-60 truncate mt-0.5">{blogger.description}</div>
          )}
        </div>
        {/* Price right */}
        <div className="text-right ml-auto">
          <div className="flex items-center gap-2 text-[10px]" style={{ color: typeColor.text }}>
            <span className="flex items-center gap-0.5">
              CPV {cartSelection.cpv.toFixed(2)} ₽
              <InfoTip align="right">Cost Per View — стоимость одного просмотра при данном формате.</InfoTip>
            </span>
            <span className="flex items-center gap-0.5">
              CPM {cpm.toFixed(0)} ₽
              <InfoTip align="right">Cost Per Mille — стоимость 1000 просмотров при данном формате.</InfoTip>
            </span>
          </div>
          <div className="text-[18px] font-display font-bold" style={{ color: typeColor.text }}>
            {fmtRub(cartSelection.price)}
          </div>
        </div>
      </div>

      {/* Row 2: format badge */}
      <div>
        <span className="inline-flex items-center h-5 px-2.5 rounded-full text-[10px] font-display font-bold"
          style={{ background: typeColor.bg, color: typeColor.text }}>
          {isPosev ? "Посев" : "Натив"} · {cartSelection.formatName} · {PLATFORM_LABELS[cartSelection.platform] ?? cartSelection.platform}
        </span>
      </div>

      {/* Row 3: metrics */}
      <div className="flex items-center gap-4 text-[11px] flex-wrap">
        <div><span className="text-ink-40">Охват</span> <span className="font-mono font-bold">{fmtNum(blogger.reach)}</span></div>
        <div className="text-ink-40">·</div>
        <div><span className="text-ink-40">Формат</span> <span className="font-mono font-bold">{cartSelection.formatName}</span></div>
        <div className="text-ink-40">·</div>
        <div className="flex items-center gap-1.5">
          <span className="text-ink-40">Дата размещения</span>
          <input
            type="date"
            required
            value={cartSelection.placementDate}
            onChange={(e) => onPlacementDateChange?.(e.target.value)}
            className="h-6 px-1.5 rounded border border-ink-12 text-[11px] font-mono outline-none focus:border-ink"
          />
        </div>
      </div>

      {/* ERID insight */}
      <div className="flex items-center gap-1.5 text-[11px] text-ink-40">
        <CheckCircle2 size={13} className="text-ink-40" />
        <span>ERID сформируется автоматически после оплаты · Готовый креатив подключён</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onRemove}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-danger hover:border-danger/40">
          <Trash2 size={12} /> Убрать
        </button>
        <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-link hover:border-link/40">
          <Sparkles size={12} /> Похожие каналы
        </button>
        <div className="flex-1" />
        <button
          onClick={() => setInboxOpen(true)}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40"
        >
          <Send size={12} /> Написать
        </button>
      </div>

      {inboxOpen && (
        <InboxModal
          open
          onClose={() => setInboxOpen(false)}
          initialBloggerId={blogger.id}
        />
      )}
    </div>
  );
}

// ─── Card Right column ────────────────────────────────────────────────────────

export function CardRight({
  type, platform, formatId, inCart, seedingLocked, nativeLocked,
  onTypeChange, onPlatformChange, onFormatChange, onCartToggle,
}: {
  type: FormatType;
  platform: PlatformKey;
  formatId: string;
  inCart: boolean;
  seedingLocked?: boolean;
  nativeLocked?: boolean;
  onTypeChange: (t: FormatType) => void;
  onPlatformChange: (p: PlatformKey) => void;
  onFormatChange: (id: string) => void;
  onCartToggle: () => void;
}) {
  const formats = getFormats(platform, type);
  const activeFormat = formats.find((f) => f.id === formatId) ?? formats[0];
  const isPosev = type === "posev";

  return (
    <div
      className="flex flex-col gap-[6px] flex-shrink-0"
      style={{
        minWidth: 180,
        maxWidth: 200,
        padding: "12px 14px",
        borderLeft: "0.5px solid rgba(28,28,28,0.12)",
      }}
    >
      <FormatToggle value={type} onChange={onTypeChange} disabledNativ={seedingLocked} disabledPosev={nativeLocked} />
      <PlatformChips type={type} value={platform} onChange={onPlatformChange} compact />

      {formats.length === 0 ? (
        <div className="text-[11px] text-ink-40">Нет форматов</div>
      ) : (
        <select
          value={activeFormat?.id ?? ""}
          onChange={(e) => onFormatChange(e.target.value)}
          className="w-full rounded-md border text-[12px] font-display font-semibold outline-none bg-white"
          style={{ padding: "5px 8px", borderColor: "rgba(28,28,28,0.12)", borderWidth: "0.5px" }}
        >
          {formats.map((f: FormatEntry) => (
            <option key={f.id} value={f.id}>{f.name} — {fmtRub(f.price)}</option>
          ))}
        </select>
      )}

      {activeFormat && (
        <div>
          <div className="text-[10px] text-ink-40 leading-none">CPV {activeFormat.cpv.toFixed(2)} ₽</div>
          <div className="text-[20px] font-display font-[500] leading-tight">{fmtRub(activeFormat.price)}</div>
        </div>
      )}

      <button
        onClick={onCartToggle}
        disabled={!activeFormat}
        className="w-full rounded-lg text-[12px] font-display font-[500] inline-flex items-center justify-center gap-1 transition-colors"
        style={{
          padding: "7px",
          ...(inCart
            ? { background: isPosev ? "#EAF3DE" : "#E6F1FB", color: isPosev ? "#27500A" : "#185FA5" }
            : { background: "#AADB54", color: "#1A3A05" }),
        }}
      >
        {inCart
          ? <><X size={12} /> Убрать</>
          : <><ShoppingCart size={12} /> В РК</>}
      </button>
    </div>
  );
}

// ─── Root BloggerCard ─────────────────────────────────────────────────────────

export function BloggerCard({
  blogger,
  context = "catalog",
  isInCart = false,
  cartSelections,
  onCartAdd,
  onCartRemove,
  cartSelection,
  onPlacementDateChange,
  seedingLocked = false,
  nativeLocked = false,
  reason,
  reason_type,
}: BloggerCardProps) {
  // Basket context: read-only
  if (context === "basket" && cartSelection) {
    return (
      <BloggerCardBasket
        blogger={blogger}
        cartSelection={cartSelection}
        onRemove={cartSelection ? () => onCartRemove?.(cartSelection) : undefined}
        onPlacementDateChange={onPlacementDateChange}
      />
    );
  }

  // Catalog / campaign: interactive
  return (
    <InteractiveCard
      blogger={blogger}
      isInCart={isInCart}
      cartSelections={cartSelections}
      onCartAdd={onCartAdd}
      onCartRemove={onCartRemove}
      seedingLocked={seedingLocked}
      nativeLocked={nativeLocked}
      reason={reason}
      reason_type={reason_type}
      context={context}
    />
  );
}

const REASON_STYLES: Record<string, { icon: LucideIcon; bg: string; text: string; iconColor: string }> = {
  audience_overlap: { icon: UserPlus,    bg: "#EEE8FF", text: "#4A1FA8", iconColor: "#6B3FD4" },
  low_cpv:         { icon: ShieldCheck,  bg: "#EAF3DE", text: "#27500A", iconColor: "#639922" },
  high_er:         { icon: Trophy,       bg: "#FFF3DC", text: "#633806", iconColor: "#D07A00" },
  category_popular:{ icon: Sparkles,     bg: "#E6F1FB", text: "#185FA5", iconColor: "#378ADD" },
  track_record:    { icon: CheckCircle2, bg: "#EAF3DE", text: "#27500A", iconColor: "#639922" },
  adjacent_affinity: { icon: AlertTriangle, bg: "#FFF3DC", text: "#8A5A00", iconColor: "#D07A00" },
  default:         { icon: Sparkles,     bg: "#EAF3DE", text: "#27500A", iconColor: "#639922" },
};

function ReasonBadge({ reason, reason_type }: { reason: string; reason_type?: string }) {
  const s = REASON_STYLES[reason_type ?? "default"] ?? REASON_STYLES.default;
  const Icon = s.icon;
  return (
    <div
      className="inline-flex items-start gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-display font-semibold leading-snug w-full"
      style={{ background: s.bg, color: s.text }}
    >
      <Icon size={12} style={{ color: s.iconColor, flexShrink: 0, marginTop: 1 }} />
      <span className="line-clamp-2">{reason}</span>
    </div>
  );
}

function InteractiveCard({
  blogger, isInCart, cartSelections, onCartAdd, onCartRemove, seedingLocked, nativeLocked, reason, reason_type, context,
}: {
  blogger: Blogger;
  isInCart: boolean;
  cartSelections?: CartSelection[];
  onCartAdd?: (sel: CartSelection) => void;
  onCartRemove?: (sel: CartSelection) => void;
  seedingLocked?: boolean;
  nativeLocked?: boolean;
  reason?: string;
  reason_type?: string;
  context?: "catalog" | "campaign" | "basket";
}) {
  const initPlatform = normalizePlatform(blogger.platform);
  const initType: FormatType = seedingLocked ? "posev" : "nativ";
  const initFormats = getFormats(initPlatform, initType);

  const [type, setType] = useState<FormatType>(initType);
  const [platform, setPlatform] = useState<PlatformKey>(initPlatform);
  const [formatId, setFormatId] = useState<string>(initFormats[0]?.id ?? "");
  const [expanded, setExpanded] = useState(false);
  const [collModalOpen, setCollModalOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const { data: membership } = useMembership();
  const inAnyCollection = (membership?.[blogger.id]?.length ?? 0) > 0;

  const isThisPlatformInCart = cartSelections != null
    ? cartSelections.some((s) => s.platform === platform && s.type === type)
    : isInCart;

  function handleTypeChange(t: FormatType) {
    if (seedingLocked && t === "nativ") return;
    if (nativeLocked && t === "posev") return;
    const p = getDefaultPlatformForType(t, platform);
    const fmts = getFormats(p, t);
    setType(t);
    setPlatform(p);
    setFormatId(fmts[0]?.id ?? "");
  }

  function handlePlatformChange(p: PlatformKey) {
    const allowed = type === "posev" ? POSEV_PLATFORMS : NATIV_PLATFORMS;
    if (!allowed.includes(p)) return;
    const fmts = getFormats(p, type);
    setPlatform(p);
    setFormatId(fmts[0]?.id ?? "");
  }

  function handleFormatChange(id: string) {
    setFormatId(id);
  }

  function handleCartToggle() {
    const formats = getFormats(platform, type);
    const fmt = formats.find((f) => f.id === formatId) ?? formats[0];
    if (!fmt) return;
    const sel: CartSelection = {
      type, platform, formatId: fmt.id, formatName: fmt.name,
      price: fmt.price, cpv: fmt.cpv, placementDate: "",
    };
    if (isThisPlatformInCart) {
      onCartRemove?.(sel);
    } else {
      onCartAdd?.(sel);
    }
  }

  const formats = getFormats(platform, type);
  const activeFormat = formats.find((f) => f.id === formatId) ?? formats[0];

  // Metrics row: derive from blogger data
  const cpvDisplay = activeFormat ? `${activeFormat.cpv.toFixed(2)} ₽` : `${blogger.cpv} ₽`;
  const erDisplay = `${blogger.er.toFixed(1)}%`;

  const isAnyInCart = isInCart || (cartSelections != null && cartSelections.length > 0);

  return (
    <div className={cn(
      "rounded-lg bg-white transition-[border-color]",
      isAnyInCart ? "border-2" : "border border-ink-12 hover:border-ink/30",
    )}
      style={isAnyInCart ? { borderColor: "#97C459" } : {}}>

      {/* Compact body ~100px */}
      <div className="flex">
        {/* LEFT */}
        <div className="flex-1 min-w-0 flex flex-col gap-[3px] px-3 py-[7px]">
          {/* Row 1: Avatar + name + meta */}
          <div className="flex items-center gap-2">
            <Avatar name={blogger.full} size={36} grad={avatarGrad(blogger.platform) as [string,string]} className="flex-shrink-0" />
            <div className="min-w-0 flex-1 overflow-hidden">
              <Link
                to={`/blogger/${blogger.id}${context === "campaign" ? "?ctx=campaign" : ""}`}
                className="font-display font-[500] text-[14px] hover:text-link truncate block leading-tight"
              >
                {blogger.full}
              </Link>
              <div className="text-[11px] text-ink-40 truncate leading-tight">
                {blogger.name} · {blogger.cats.join(", ")}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setInboxOpen(true); }}
              title="Написать блогеру"
              className="flex-shrink-0 text-ink-40 hover:text-ink transition-colors"
            >
              <Send size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setCollModalOpen(true); }}
              title={inAnyCollection ? "В подборках — изменить" : "Добавить в подборку"}
              className={cn("flex-shrink-0 hover:text-[#639922]", inAnyCollection ? "text-[#639922]" : "text-ink-40")}
            >
              {inAnyCollection
                ? <BookmarkCheck size={14} />
                : <Bookmark size={14} />}
            </button>
          </div>

          {/* Description */}
          {blogger.description && (
            <div className="text-[11.5px] text-ink-60 truncate leading-snug">{blogger.description}</div>
          )}

          {reason && <ReasonBadge reason={reason} reason_type={reason_type} />}

          {/* Row 2: Metrics */}
          <div className="flex items-center flex-wrap gap-y-1">
            <InlineMetric v={fmtNum(blogger.reach)} label="просмотры" tip="Среднее количество просмотров одного поста за последние 30 дней" />
            <div className="mx-2 self-stretch" style={{ width: "0.5px", background: "rgba(28,28,28,0.12)" }} />
            <InlineMetric v={fmtNum(Math.round(blogger.reach * 0.68))} label="рекл. охват" tip="Расчётный рекламный охват публикации — оценочно 68% от среднего органического просмотра. Источник и формула уточняются у аналитики. TODO" />
            <div className="mx-2 self-stretch" style={{ width: "0.5px", background: "rgba(28,28,28,0.12)" }} />
            <InlineMetric v={erDisplay} label="ER" tip="Engagement Rate — отношение реакций и комментариев к числу просмотров. Чем выше, тем активнее аудитория." />
            <div className="mx-2 self-stretch" style={{ width: "0.5px", background: "rgba(28,28,28,0.12)" }} />
            <InlineMetric v={cpvDisplay} label="CPV" tip="Cost Per View — стоимость одного просмотра при текущем выбранном формате размещения." />
            <div className="mx-2 self-stretch" style={{ width: "0.5px", background: "rgba(28,28,28,0.12)" }} />
            <InlineMetric v={`${1 + (mockHash(blogger.id + "t") % 5)} ч`} label="ответ" tip="Среднее время ответа блогера на запрос о сотрудничестве." />
          </div>

          {/* Row 3: Mini ЦА / гео */}
          {(() => {
            const male = mockMale(blogger.id);
            const female = 100 - male;
            const topCityPct = 38 + (mockHash(blogger.id + "g") % 12);
            return (
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-10 h-[3px] rounded-full overflow-hidden flex flex-shrink-0">
                  <div style={{ width: `${male}%`, background: "#5B8DEF" }} className="h-full" />
                  <div className="h-full flex-1" style={{ background: "#F07AAC" }} />
                </div>
                <span className="text-[10px] text-ink-40 truncate leading-none">
                  М {male}% · Ж {female}% · Москва {topCityPct}%
                </span>
              </div>
            );
          })()}

          {/* Row 4: AI insight + expand */}
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0 overflow-hidden">
              <AiInsight formatId={formatId} type={type} />
            </div>
            <button
              onClick={() => setExpanded((e) => !e)}
              className="flex-shrink-0 inline-flex items-center gap-0.5 text-[11px] text-link hover:underline whitespace-nowrap"
            >
              {expanded ? <><ChevronUp size={11} /> Свернуть</> : <><ChevronDown size={11} /> Подробнее</>}
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <CardRight
          type={type}
          platform={platform}
          formatId={formatId}
          inCart={isThisPlatformInCart}
          seedingLocked={seedingLocked}
          nativeLocked={nativeLocked}
          onTypeChange={handleTypeChange}
          onPlatformChange={handlePlatformChange}
          onFormatChange={handleFormatChange}
          onCartToggle={handleCartToggle}
        />
      </div>

      {/* Expanded section */}
      {expanded && (
        <CardExpanded
          blogger={blogger}
          onClose={() => setExpanded(false)}
        />
      )}

      {collModalOpen && (
        <AddToCollectionModal
          bloggerId={blogger.id}
          bloggerName={blogger.full}
          onClose={() => setCollModalOpen(false)}
        />
      )}
      {inboxOpen && (
        <InboxModal
          open
          onClose={() => setInboxOpen(false)}
          initialBloggerId={blogger.id}
        />
      )}
    </div>
  );
}
